// ============================================================================
// API CLIENT
// ----------------------------------------------------------------------------
// Every call goes to the application service, which is the same origin as this
// bundle in a deployment and proxied by Vite in development. Nothing here
// talks to the gateway: the browser never holds a gateway credential, and the
// prompts the answer is built from are not something this side of the wire is
// ever shown. That asymmetry is the product.
//
// The mock layer this replaces called out to a per-step pipeline
// (/api/moderation, /api/questions/title, …). It is one call now — POST
// /api/ask — because the orchestration moved to the server, where it can
// survive the tab closing. Progress comes back as server-sent events.
// ============================================================================

import type {
  Audience,
  AnalyticsDay,
  AnalyticsOverview,
  AnswerDocument,
  AskFilters,
  CitedPage,
  DateRange,
  DocumentPageDetail,
  DocumentUsage,
  LibrarySearchResult,
  PastLibrarySearch,
  AskRequest,
  DocumentDetail,
  LibraryDocument,
  ManagedTag,
  Membership,
  Invitation,
  ManagedUser,
  QuestionCandidate,
  QuestionUsage,
  TagInput,
  UserSettings,
  QuestionComment,
  QuestionDetail,
  QuestionRating,
  RatingView,
  QuestionSource,
  PreviousQuestion,
  Role,
  Session,
  UserUsage,
} from "@/types";

// ---------------------------------------------------------------------------
// Token plumbing
// ---------------------------------------------------------------------------
//
// Two tokens, with different jobs (ADR 0007). The identity provider's ID token
// says who the person is and is presented exactly once per session, to
// POST /api/session/tenant. What comes back is a session token naming one org,
// and that is what every other call carries. The server refuses an ID token
// anywhere else, because it names a person and not an org.

type TokenProvider = () => string | null;
type SessionRefresher = () => Promise<string | null>;

let getToken: TokenProvider = () => null;
let refreshSession: SessionRefresher | null = null;

/**
 * Installed once, by SessionProvider. `refresh` mints a new session for the
 * current org; a request that gets a 401 calls it once and retries, which is
 * what carries a person across the session's expiry without a visible error.
 */
export function setSessionHandlers(get: TokenProvider, refresh: SessionRefresher | null): void {
  getToken = get;
  refreshSession = refresh;
}

/** Thrown for any non-2xx response, carrying the server's typed error code. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Machine-readable context for the few codes the app acts on. */
  readonly details: Record<string, string>;

  constructor(status: number, code: string, message: string, details: Record<string, string> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * The org requires its own identity provider and this sign-in did not come
   * through it. `details.provider` is the one to use.
   */
  get needsSSO(): boolean {
    return this.status === 403 && this.code === "sso_required";
  }

  /** A 401 that survived a session refresh: the identity itself has lapsed. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * The person is signed in but has no membership here — a person problem,
   * not a token problem. Its own code, because an ordinary permission refusal
   * is also a 403 and wants a different message.
   */
  get needsInvitation(): boolean {
    return this.status === 403 && this.code === "no_membership";
  }
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  // The server always sends the typed envelope, but a proxy or a load balancer
  // in front of it may not — so a non-JSON body is handled rather than
  // surfacing as "Unexpected token < in JSON".
  let code = "unknown";
  let message = fallback;
  let details: Record<string, string> = {};
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string; details?: Record<string, string> };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details ?? details;
    }
  } catch {
    /* keep the fallback */
  }
  return new ApiError(res.status, code, message, details);
}

/** As many documents as one list call returns; the server's MaxListLimit. */
export const LIBRARY_LIMIT = 10_000;

export interface DocumentList {
  documents: LibraryDocument[];
  /** Every document in the library, which is more than `documents` only past LIBRARY_LIMIT. */
  total: number;
}

/** The last answer per document-list URL, for `api.documents`' If-None-Match. */
const lastDocumentLists = new Map<string, { etag: string; list: DocumentList }>();

/**
 * fetch with the session token, and one refresh-and-retry on a 401.
 *
 * Every body this client sends is a string, so sending it twice is safe.
 */
async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return fetch(path, { ...init, headers });
  };

  const res = await send(getToken());
  if (res.status !== 401 || !refreshSession) return res;
  const fresh = await refreshSession();
  return fresh ? send(fresh) : res;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authorizedFetch(path, init);
  if (!res.ok) throw await toApiError(res, `Request failed with status ${res.status}.`);
  return (await res.json()) as T;
}

/**
 * A request whose response carries no body.
 *
 * Separate from `request` rather than `request<void>`, because a generic
 * documented to return parsed JSON should not be instantiated with a type that
 * has no value — and the 204 branch belongs with the callers that expect one.
 */
async function requestNoContent(path: string, init: RequestInit = {}): Promise<void> {
  const res = await authorizedFetch(path, init);
  if (res.ok) return;
  throw await toApiError(res, `Request failed with status ${res.status}.`);
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export interface MintedSession {
  token: string;
  expiresAt: string;
  org: { id: string; slug: string; name: string };
  role: Role;
  memberships: Membership[];
}

/**
 * Exchange the identity token for a session.
 *
 * With no `orgId` the server chooses: the org this hostname belongs to if the
 * person is in it, otherwise their first. With one, it is that org or a 403 —
 * the server checks membership and never falls back silently.
 */
export async function mintSession(idToken: string, orgId?: string): Promise<MintedSession> {
  const res = await fetch("/api/session/tenant", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(orgId ? { "Content-Type": "application/json" } : {}),
    },
    body: orgId ? JSON.stringify({ orgId }) : undefined,
  });
  if (!res.ok) throw await toApiError(res, `Signing in failed with status ${res.status}.`);
  return (await res.json()) as MintedSession;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const api = {
  session(): Promise<Session> {
    return request<Session>("/api/session");
  },

  filters(): Promise<AskFilters> {
    return request<AskFilters>("/api/filters");
  },

  /**
   * The library's documents. `limit` defaults to the server's page of 100;
   * the Library page asks for all of them (LIBRARY_LIMIT).
   *
   * Revalidated rather than re-downloaded: the library changes a few times a
   * week and the page polls it every fifty seconds, so the last answer and its
   * ETag are kept and sent back as If-None-Match, and an unchanged library
   * comes back as a bodiless 304. Kept in memory, not in the browser's HTTP
   * cache, which would leave the library on disk after sign-out; the server
   * says no-store for that reason. An org switch reloads the page, which
   * empties this; the tag is per org besides.
   */
  async documents(
    params: { search?: string; tags?: string[]; audiences?: string[]; limit?: number } = {},
  ): Promise<DocumentList> {
    const q = new URLSearchParams();
    if (params.search) q.set("q", params.search);
    for (const t of params.tags ?? []) q.append("tag", t);
    for (const a of params.audiences ?? []) q.append("audience", a);
    if (params.limit) q.set("limit", String(params.limit));
    const path = `/api/documents${q.toString() ? `?${q}` : ""}`;

    const last = lastDocumentLists.get(path);
    const res = await authorizedFetch(path, {
      headers: last ? { "If-None-Match": last.etag } : undefined,
    });
    if (res.status === 304 && last) return last.list;
    if (!res.ok) throw await toApiError(res, `Request failed with status ${res.status}.`);
    const list = (await res.json()) as DocumentList;
    const etag = res.headers.get("ETag");
    if (etag) lastDocumentLists.set(path, { etag, list });
    return list;
  },

  document(id: string): Promise<DocumentDetail> {
    return request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}`);
  },

  /**
   * A short-lived signed URL for the original upload.
   *
   * Fetched rather than linked to: a plain link is a navigation, and a
   * navigation carries no Authorization header, so it could never reach an
   * authenticated route.
   */
  documentSource(id: string): Promise<{ url: string }> {
    return request(`/api/documents/${encodeURIComponent(id)}/source`, {
      headers: { Accept: "application/json" },
    });
  },

  /**
   * The document's PDF, as bytes for pdf.js.
   *
   * Streamed by the server from Box or from the stored original (see
   * internal/httpapi/pdf.go). Fetched with the session token for the same
   * reason as `documentSource`; a 404 means this document has no PDF to show,
   * and the page falls back to the rasterised page images.
   */
  async documentPdf(id: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    const res = await authorizedFetch(`/api/documents/${encodeURIComponent(id)}/pdf`, { signal });
    if (!res.ok) throw await toApiError(res, "The PDF could not be loaded.");
    return res.arrayBuffer();
  },

  /**
   * Plain-language search over the library.
   *
   * This embeds the text through the gateway and searches the vectors, so it
   * finds documents by meaning rather than by title. It costs a model call per
   * search, which is why it is a submitted form rather than search-as-you-type.
   */
  searchLibrary(query: string, opts: { audiences?: string[]; topics?: string[] } = {}): Promise<{
    results: LibrarySearchResult[];
  }> {
    return request("/api/library/search", {
      method: "POST",
      body: JSON.stringify({ query, ...opts }),
    });
  },

  /** The caller's own library searches, newest first. The server records them as they run. */
  librarySearchHistory(limit = 50): Promise<{ searches: PastLibrarySearch[] }> {
    return request(`/api/library/searches?limit=${limit}`);
  },

  /**
   * Correct a document's catalogue metadata.
   *
   * Never re-runs the pipeline. Year, audience and topics are read per
   * question by the ranking function, so a correction takes effect on the next
   * question with no re-embedding; the title is display-only in retrieval.
   */
  updateDocument(
    id: string,
    patch: {
      title?: string;
      year?: number;
      audience?: Audience;
      topics?: string[];
      docType?: string;
      /**
       * Revert topics to what the document processor extracted — ERIKA's
       * *Reset Tags*. A flag rather than the caller sending the extracted list
       * back, because the caller should not be able to claim a set is "what
       * was extracted": that fact belongs to the ingestion API.
       */
      resetTopics?: boolean;
    },
  ): Promise<DocumentDetail> {
    return request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  removeDocument(id: string): Promise<void> {
    return requestNoContent(`/api/documents/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  questions(params: { search?: string; mine?: boolean } = {}): Promise<{
    questions: PreviousQuestion[];
    total: number;
  }> {
    const q = new URLSearchParams();
    if (params.search) q.set("q", params.search);
    if (params.mine) q.set("mine", "true");
    const suffix = q.toString() ? `?${q}` : "";
    return request(`/api/questions${suffix}`);
  },

  question(id: string): Promise<QuestionDetail> {
    return request<QuestionDetail>(`/api/questions/${encodeURIComponent(id)}`);
  },

  /** What the retrieval actually put in front of the model. */
  questionSources(id: string): Promise<{ sources: QuestionSource[] }> {
    return request(`/api/questions/${encodeURIComponent(id)}/sources`);
  },

  /**
   * The optimiser's rewrites of the question.
   *
   * ERIKA's Optimized Questions panel. These have been persisted since the
   * pipeline was written and nothing read them back — they answer the question
   * a reader actually asks when an answer misses, which is "did it search for
   * what I meant?", and the answer itself cannot.
   */
  questionCandidates(id: string): Promise<{ candidates: QuestionCandidate[] }> {
    return request(`/api/questions/${encodeURIComponent(id)}/candidates`);
  },

  /**
   * What one question cost at the gateway, by stage.
   *
   * Summed from activity_events, which holds counts, durations and enums and
   * no text at all — so this route cannot carry a prompt or an answer however
   * it is called.
   */
  questionUsage(id: string): Promise<QuestionUsage> {
    return request(`/api/questions/${encodeURIComponent(id)}/usage`);
  },

  /**
   * The single-score rating.
   *
   * Superseded in the UI by `saveQuestionRating`, and kept because the route
   * is still served: it writes only the overall dimension and leaves a
   * considered six-dimension review intact underneath it.
   */
  rateQuestion(id: string, rating: number, comment = ""): Promise<void> {
    return requestNoContent(`/api/questions/${encodeURIComponent(id)}/rating`, {
      method: "PUT",
      body: JSON.stringify({ rating, comment }),
    });
  },

  deleteQuestion(id: string): Promise<void> {
    return requestNoContent(`/api/questions/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  /**
   * Collect an answer whose run was interrupted.
   *
   * A deploy during the ninety seconds an answer takes leaves the question at
   * `generating_answer` with a gateway operation that is still perfectly
   * healthy. This finishes it rather than asking — and paying — again.
   */
  resumeQuestion(id: string): Promise<QuestionDetail> {
    return request<QuestionDetail>(`/api/questions/${encodeURIComponent(id)}/resume`, {
      method: "POST",
    });
  },

  /**
   * The page images an answer's citations point at.
   *
   * Separate from `question()` because the URLs inside are signed and
   * short-lived: folding them into the question object would put an expiring
   * value inside a cache entry that nothing invalidates, and the reader would
   * come back to a page of broken images after an hour on another tab.
   */
  citedPages(id: string): Promise<{ pages: CitedPage[] }> {
    return request(`/api/questions/${encodeURIComponent(id)}/cited-pages`);
  },

  // ── ratings and discussion ──────────────────────────────────

  /** This viewer's rating and the aggregate, in one response. */
  questionRatings(id: string): Promise<RatingView> {
    return request(`/api/questions/${encodeURIComponent(id)}/ratings`);
  },

  /**
   * Save the multi-dimension rating.
   *
   * PUT, not POST: one row per person per question, so sending it twice must
   * leave the same state. The response carries the recomputed aggregate, so
   * the page does not re-fetch to show the average it just moved.
   */
  saveQuestionRating(id: string, rating: QuestionRating): Promise<RatingView> {
    return request(`/api/questions/${encodeURIComponent(id)}/ratings`, {
      method: "PUT",
      body: JSON.stringify(rating),
    });
  },

  questionComments(id: string): Promise<{ comments: QuestionComment[] }> {
    return request(`/api/questions/${encodeURIComponent(id)}/comments`);
  },

  /** Returns the whole thread, since someone may have posted while typing. */
  addQuestionComment(id: string, body: string): Promise<{ comments: QuestionComment[] }> {
    return request(`/api/questions/${encodeURIComponent(id)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  },

  deleteQuestionComment(questionId: string, commentId: string): Promise<void> {
    return requestNoContent(
      `/api/questions/${encodeURIComponent(questionId)}/comments/${encodeURIComponent(commentId)}`,
      { method: "DELETE" },
    );
  },

  /** One page, which is what the page viewer's arrows fetch. */
  documentPage(documentId: string, page: number): Promise<DocumentPageDetail> {
    return request(`/api/documents/${encodeURIComponent(documentId)}/pages/${page}`);
  },

  // ── administration ──────────────────────────────────────────

  users(): Promise<{ users: ManagedUser[] }> {
    return request("/api/users");
  },

  invitations(): Promise<{ invitations: Invitation[] }> {
    return request("/api/invitations");
  },

  invite(email: string, role: Role): Promise<{ invitations: Invitation[] }> {
    return request("/api/invitations", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    });
  },

  cancelInvitation(id: string): Promise<{ invitations: Invitation[] }> {
    return request(`/api/invitations/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  /**
   * Change a role, withdraw access, or restore it.
   *
   * The response is the whole list rather than the one row, because a change
   * of owner can move the last-owner constraint for every other row and a
   * client patching one entry in place would show stale controls on the rest.
   */
  updateUser(id: string, patch: { role?: Role; revoked?: boolean }): Promise<{ users: ManagedUser[] }> {
    return request(`/api/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  tags(): Promise<{ tags: ManagedTag[] }> {
    return request("/api/tags");
  },

  /**
   * Add a tag, or edit the one already carrying that name.
   *
   * One call rather than a create and an update, because the vocabulary is
   * keyed on the name someone types: the form cannot know whether "Retention"
   * is new without asking first, and asking would be a round trip to guard
   * against a case that is not an error.
   */
  saveTag(input: TagInput): Promise<ManagedTag> {
    return request("/api/tags", { method: "POST", body: JSON.stringify(input) });
  },

  setTagActive(id: string, active: boolean): Promise<void> {
    return requestNoContent(`/api/tags/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    });
  },

  // ── my settings ─────────────────────────────────────────────

  /** This person's Ask-form defaults. Never anyone else's: the row is keyed
   *  on the user id in the token, so there is no identifier to get wrong. */
  settings(): Promise<UserSettings> {
    return request("/api/settings");
  },

  /**
   * Save them.
   *
   * The whole object, not a patch. Every control in the drawer is bound to a
   * field, and a partial write would let two open tabs each silently revert
   * the other's change to a control neither touched.
   */
  saveSettings(s: UserSettings): Promise<UserSettings> {
    return request("/api/settings", { method: "PUT", body: JSON.stringify(s) });
  },

  // ── analytics ───────────────────────────────────────────────

  analyticsOverview(range: DateRange): Promise<AnalyticsOverview> {
    return request(`/api/analytics/overview?${rangeQuery(range)}`);
  },

  analyticsActivity(range: DateRange): Promise<{ days: AnalyticsDay[] }> {
    return request(`/api/analytics/activity?${rangeQuery(range)}`);
  },

  analyticsDocuments(range: DateRange, limit = 20): Promise<{ documents: DocumentUsage[] }> {
    return request(`/api/analytics/documents?${rangeQuery(range)}&limit=${limit}`);
  },

  analyticsUsers(range: DateRange): Promise<{ users: UserUsage[] }> {
    return request(`/api/analytics/users?${rangeQuery(range)}`);
  },
};

function rangeQuery(range: DateRange): string {
  return new URLSearchParams({ start: range.start, end: range.end }).toString();
}

// ---------------------------------------------------------------------------
// Ask — one request, progress over server-sent events
// ---------------------------------------------------------------------------

export interface AskEvent {
  stage: string;
  caption?: string;
  done?: boolean;
  error?: string;
  code?: string;
  question_id?: string;
}

/**
 * Submit a question and stream progress.
 *
 * `fetch` rather than `EventSource`, because EventSource cannot send a request
 * body or an Authorization header — it only does GET with cookies.
 *
 * The returned promise resolves with the terminal event. Aborting the signal
 * stops the *stream*, not the run: the server deliberately detaches the
 * pipeline from the request, so a closed tab still produces an answer the user
 * has already paid for.
 */
export async function ask(
  body: AskRequest,
  onEvent: (event: AskEvent) => void,
  signal?: AbortSignal,
): Promise<AskEvent> {
  const res = await authorizedFetch("/api/ask", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    throw await toApiError(res, `The question could not be submitted (${res.status}).`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let last: AskEvent = { stage: "validating_inputs" };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Events are separated by a blank line. Anything after the last separator
    // is a partial frame and stays in the buffer.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";

    for (const frame of frames) {
      for (const line of frame.split("\n")) {
        // ": keepalive" comments hold the connection open through the long
        // quiet stretch while the answer generates.
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice(6)) as AskEvent;
          last = event;
          onEvent(event);
        } catch {
          /* a malformed frame is dropped; the question row is the record */
        }
      }
    }
  }
  return last;
}

export type { AnswerDocument };

/**
 * Open a document's original in a new tab.
 *
 * The tab is opened before the URL is fetched, while the click still counts as
 * a user gesture; opened afterwards, Safari and Firefox treat it as a popup and
 * block it.
 */
export async function openDocumentSource(id: string): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const { url } = await api.documentSource(id);
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (e) {
    tab?.close();
    throw e;
  }
}
